import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AlertController, ModalController, Platform, ToastController } from '@ionic/angular';
import { Abonnement } from 'src/app/models/abonnement';
import { Payment } from 'src/app/models/payment';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/micro-services/user/models/user';
import { UserEleve } from 'src/app/micro-services/user/models/user-eleve';
import { UserCoach } from 'src/app/micro-services/user/models/user-coach';
import { Directory, Encoding, Filesystem, FilesystemDirectory } from '@capacitor/filesystem';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener';

(pdfMake as any).vfs = pdfFonts?.pdfMake?.vfs;

@Component({
  selector: 'app-form-bills',
  templateUrl: './form-bills.component.html',
  styleUrls: ['./form-bills.component.scss'],
})
export class FormBillsComponent implements OnInit {

  @Input() client: User;
  @Input() abonnements: Abonnement[];


  coach: User;

  billForm: FormGroup;
  endDate: Date = new Date();
  startDate: Date = this.endDate;
  payments: Payment[] = [];
  totalBill: number;
  dateActuelle: Date;
  billNumber: string;

  constructor(
    private builder: FormBuilder,
    private userService: UserService,
    private apiProvider: ApiProviderService,
    private modalCtrl: ModalController,
    private plt: Platform,
    private toastr: ToastController,
    private readonly alertCtrl : AlertController
  ) {
    this.dateActuelle = new Date()
  }

  ngOnInit() {
    this.coach = this.userService.getUser()
    this.startDate.setMonth(this.startDate.getMonth() - 1);
    this.billForm = this.builder.group({
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
      billNumber: [this.billNumber, Validators.required]
    });


  }

  get coa(): UserCoach {
    return this.coach as UserCoach;
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  async validate() {
    if(!this.coa.compagnyNumber || !this.coa.email || !this.coa.address || !this.coa.firstName || !this.coa.name){
    const alert = await this.alertCtrl.create({
      header: 'Confirmation',
      message: `Il vous manque des informations personnelles, veuillez les ajouter dans mon compte -> informations personnelles`,
      buttons: [ {
          text: 'Ok',
          handler: () => {
            return
          }
        }
      ]
    });

    await alert.present();
    }
    
    if (this.billForm.invalid) {
      this.billForm.markAllAsTouched();
      return;
    }

    if (!this.endDate) {
      this.endDate = new Date();
    }

    if (!this.coa.compagnyNumber || !this.coa.firstName || !this.coa.name) {
      const toast = await this.toastr.create({
        message: "Pour générer des factures, nous avons besoin de plus d'informations. Veuillez fournir les détails requis pour compléter la génération de la facture, vous pouvez ajouter ces informations depuis l'onglet profil du menu de l'application. (Numéro de SIRET, Nom, Prénom)",
        duration: 5000,
        buttons: [
          {
            text: 'Compris',
            role: 'cancel'
          }
        ]
      });
      await toast.present();
      this.modalCtrl.dismiss()
      return
    }

    this.endDate.setHours(23, 59, 59, 999);
    this.startDate.setHours(0, 1, 0, 0);

    this.apiProvider.getPaymentsByClientByDate(this.client.id, this.startDate, this.endDate).subscribe(
      async (res: any) => {
        // Filtrer les paiements pour ne conserver que ceux dont le type est 0
        const filteredPayments = res.filter((payment: any) => payment.type === 0);
        // Ajouter les paiements filtrés à la liste existante
        this.payments = [...this.payments, ...filteredPayments];
        this.calculateTotal();

        const documentDefinition = this.getDocumentDefinition();
        var pdf = pdfMake.createPdf(documentDefinition)
        if (this.plt.is('cordova')) {
          pdf.getBase64(async (base64) => {
            try {
              const path = `facture${this.billForm.controls["billNumber"].value}.pdf`;

              await Filesystem.writeFile({
                path,
                data: base64,
                directory: Directory.Documents,
                // Omit the encoding property for base64 data
              });

              const fileOpenerOptions: FileOpenerOptions = {
                filePath: `${(await Filesystem.getUri({ path, directory: Directory.Documents })).uri}`,
                contentType: 'application/pdf',
                openWithDefault: true,
              };
              await FileOpener.open(fileOpenerOptions);

            } catch (e) {
              console.log('Error opening file', e);
            }
          });
        } else {
          pdf.download(`facture${this.billForm.controls["billNumber"].value}.pdf`);
        }
        this.modalCtrl.dismiss("bueno", 'confirm');
      }
    );
  }

  getDocumentDefinition() {
    return {
      content: [
        {
          columns: [
            [
              { text: `${this.coach.firstName} ${this.coach.name}`, style: 'header' },
              { text: this.coa.compagnyNumber },
              { text: this.coach.email },
              { text: this.coach.phoneNumber }
            ],
            [
              {
                text: `Facture n°${this.billForm.controls["billNumber"].value}`,
                alignment: 'right'
              },
              {
                text: `Date d'émission : ${this.dateActuelle.toLocaleDateString()}`,
                alignment: 'right'
              }
            ]
          ]
        },
        { text: '\n' },
        {
          columns: [
            [],
            [
              { text: 'Facturé à :', style: 'subheader' },
              { text: `${this.client.firstName} ${this.client.name}` },
              { text: this.client.email }
            ]
          ]
        },
        { text: '\n' },
        { text: `Période du ${this.startDate.toLocaleDateString()} au ${this.endDate.toLocaleDateString()}` },
        { text: '\n' },
        this.getPaymentTable(),
        { text: `Total HT : ${this.totalBill} €`, style: 'total', alignment: 'right' },
        { text: '\nTVA Non applicable, article 239B du CGI', style: 'note' }
      ],
      styles: {
        header: {
          fontSize: 14,
          bold: true
        },
        subheader: {
          fontSize: 12,
          bold: true
        },
        total: {
          bold: true,
          margin: [0, 10, 0, 0]
        },
        note: {
          fontSize: 10,
          italics: true
        }
      }
    };
  }

  getPaymentTable() {
    return {
      table: {
        widths: ['*', 'auto', 'auto'],
        body: [
          ['Date du Paiement', 'Montant', 'Méthode de Paiement'],
          ...this.payments.map(payment => ([
            new Date(payment.datePaiement).toLocaleDateString(),
            `${payment.montantPaye} €`,
            payment.paymentMethod
          ]))
        ]
      }
    };
  }

  updateStartDate(event: any) {
    this.startDate = new Date(event.detail.value);
  }

  updateEndDate(event: any) {
    this.endDate = new Date(event.detail.value);
  }

  calculateTotal() {
    this.totalBill = this.payments.reduce((acc, payment) => acc + payment.montantPaye, 0);
  }

}
